import { useState, useMemo, useCallback, useRef } from 'react';
import {
  makeStyles,
  tokens,
  Body1,
  Body2,
  Caption1,
  Badge,
  Button,
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogTrigger,
  Input,
  Textarea,
  Dropdown,
  Option,
  Field,
  Spinner,
  MessageBar,
  MessageBarBody,
  Image,
  Divider,
  Tab,
  TabList,
} from '@fluentui/react-components';
import { PageTitle } from '@/components/PageTitle';
import {
  Add24Regular,
  Camera24Regular,
  ArrowDownload24Regular,
  Mail24Regular,
  Checkmark24Regular,
  Dismiss24Regular,
} from '@fluentui/react-icons';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { DataTable, type Column } from '@/components/DataTable';
import { useSharePointList } from '@/hooks/useSharePointList';
import type {
  Expense,
  ExpenseCategory,
  ExpenseStatus,
  SPListItem,
} from '@/models/types';
import {
  formatCurrency,
  formatDate,
} from '@/utils/formatters';
import { EXPENSE_CATEGORIES, DEFAULT_CURRENCY, BILL_PHOTO_MAX_WIDTH, BILL_PHOTO_QUALITY } from '@/utils/constants';
import { useAppContext } from '@/store/appContext';
import { canApproveExpenses, canViewAllExpenses, canEmailReports } from '@/utils/permissions';
import { resizeImage } from '@/graph/fileService';
import { uploadFile } from '@/graph/fileService';
import { sendMail } from '@/graph/mailService';

const STATUS_COLORS: Record<ExpenseStatus, 'warning' | 'success' | 'danger' | 'informative'> = {
  Draft: 'informative',
  Submitted: 'warning',
  Approved: 'success',
  Rejected: 'danger',
};

const useStyles = makeStyles({
  page: { display: 'flex', flexDirection: 'column', gap: '20px' },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap' as const,
    gap: '12px',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  row: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '16px',
    '@media (max-width: 480px)': {
      gridTemplateColumns: '1fr',
    },
  },
  photoArea: {
    border: `2px dashed ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    padding: '20px',
    textAlign: 'center' as const,
    cursor: 'pointer',
    '@media (max-width: 480px)': {
      padding: '12px',
    },
  },
  photoPreview: {
    maxWidth: '100%',
    maxHeight: '200px',
    borderRadius: tokens.borderRadiusMedium,
  },
  approvalActions: {
    display: 'flex',
    gap: '8px',
  },
});

export function Expenses() {
  const styles = useStyles();
  const { graphClient, currentUser, listIds } = useAppContext();
  const canApprove = currentUser ? canApproveExpenses(currentUser.role) : false;
  const canViewAll = currentUser ? canViewAllExpenses(currentUser.role) : false;
  const canEmail = currentUser ? canEmailReports(currentUser.role) : false;

  const { items: expenses, loading, create, update, refresh } =
    useSharePointList<Expense>('OC_Expenses', { autoFetch: !!listIds });

  const [showForm, setShowForm] = useState(false);
  const [activeTab, setActiveTab] = useState<string>('my');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [formData, setFormData] = useState({
    Amount: 0,
    Currency: DEFAULT_CURRENCY,
    Category: 'Other' as ExpenseCategory,
    Description: '',
    ExpenseDate: new Date().toISOString().split('T')[0],
    NeedsApproval: true,
    Notes: '',
  });

  const myExpenses = useMemo(
    () =>
      expenses.filter(
        (e) => e.fields.EmployeeEmail === currentUser?.email,
      ),
    [expenses, currentUser],
  );

  const pendingApproval = useMemo(
    () =>
      expenses.filter(
        (e) => e.fields.Status === 'Submitted' && e.fields.NeedsApproval,
      ),
    [expenses],
  );

  const allExpenses = expenses;

  const displayExpenses = useMemo(() => {
    if (activeTab === 'approval' && canApprove) return pendingApproval;
    if (activeTab === 'all' && canViewAll) return allExpenses;
    return myExpenses;
  }, [activeTab, canApprove, canViewAll, pendingApproval, allExpenses, myExpenses]);

  const handlePhotoCapture = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setPhotoFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setPhotoPreview(ev.target?.result as string);
    reader.readAsDataURL(file);
  };

  // Native camera handler — uses Capacitor on mobile shell, falls back to web input
  const handleNativeCamera = async () => {
    const { getCameraPhoto, isNativeShell } = await import('@/utils/nativeBridge');
    if (!isNativeShell()) {
      // On web, use the standard <input type=file capture=environment>
      fileInputRef.current?.click();
      return;
    }
    const blob = await getCameraPhoto();
    if (!blob) return;
    const file = new File([blob], `bill_${Date.now()}.jpg`, {
      type: 'image/jpeg',
    });
    setPhotoFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setPhotoPreview(ev.target?.result as string);
    reader.readAsDataURL(blob);
  };

  const handleSubmit = async () => {
    if (!graphClient || !currentUser) return;
    if (formData.Amount <= 0) {
      setError('Amount must be greater than 0');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      let billPhotoUrl = '';

      // Upload photo if provided
      if (photoFile) {
        const resized = await resizeImage(
          photoFile,
          BILL_PHOTO_MAX_WIDTH,
          BILL_PHOTO_QUALITY,
        );
        const buffer = await resized.arrayBuffer();
        const fileName = `bill_${Date.now()}_${currentUser.email.split('@')[0]}.jpg`;
        billPhotoUrl = await uploadFile(graphClient, fileName, buffer);
      }

      await create({
        EmployeeEmail: currentUser.email,
        Amount: formData.Amount,
        Currency: formData.Currency,
        Category: formData.Category,
        Description: formData.Description,
        ExpenseDate: formData.ExpenseDate,
        BillPhotoUrl: billPhotoUrl,
        Status: formData.NeedsApproval ? 'Submitted' : 'Approved',
        ApproverEmail: '',
        NeedsApproval: formData.NeedsApproval,
        Notes: formData.Notes,
      });

      setShowForm(false);
      setPhotoPreview(null);
      setPhotoFile(null);
      setSuccess('Expense submitted successfully');
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit');
    } finally {
      setSubmitting(false);
    }
  };

  const handleApprove = async (item: SPListItem<Expense>) => {
    await update(item.id, {
      Status: 'Approved',
      ApproverEmail: currentUser?.email ?? '',
    });
    await refresh();
  };

  const handleReject = async (item: SPListItem<Expense>) => {
    await update(item.id, {
      Status: 'Rejected',
      ApproverEmail: currentUser?.email ?? '',
    });
    await refresh();
  };

  const generateExcelReport = useCallback(
    async (expensesToExport: SPListItem<Expense>[]) => {
      const workbook = new ExcelJS.Workbook();
      const sheet = workbook.addWorksheet('Expense Report');

      // Header
      sheet.mergeCells('A1:F1');
      const titleCell = sheet.getCell('A1');
      titleCell.value = 'Expense Report';
      titleCell.font = { size: 16, bold: true };
      titleCell.alignment = { horizontal: 'center' };

      sheet.mergeCells('A2:F2');
      sheet.getCell('A2').value = `Generated: ${new Date().toLocaleDateString('en-GB')}`;
      sheet.getCell('A2').alignment = { horizontal: 'center' };

      // Column headers
      const headers = ['Date', 'Employee', 'Category', 'Description', 'Amount', 'Status'];
      const headerRow = sheet.addRow(headers);
      headerRow.eachCell((cell) => {
        cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FF0078D4' },
        };
        cell.alignment = { horizontal: 'center' };
      });

      // Data rows
      let totalAmount = 0;
      for (const exp of expensesToExport) {
        const row = sheet.addRow([
          formatDate(exp.fields.ExpenseDate),
          exp.fields.EmployeeEmail,
          exp.fields.Category,
          exp.fields.Description,
          exp.fields.Amount,
          exp.fields.Status,
        ]);
        totalAmount += exp.fields.Amount;

        // Color code status
        const statusCell = row.getCell(6);
        if (exp.fields.Status === 'Approved') {
          statusCell.font = { color: { argb: 'FF107C10' } };
        } else if (exp.fields.Status === 'Rejected') {
          statusCell.font = { color: { argb: 'FFD13438' } };
        }
      }

      // Total row
      const totalRow = sheet.addRow(['', '', '', 'TOTAL', totalAmount, '']);
      totalRow.getCell(4).font = { bold: true };
      totalRow.getCell(5).font = { bold: true };
      totalRow.getCell(5).numFmt = '£#,##0.00';

      // Format amount column
      sheet.getColumn(5).numFmt = '£#,##0.00';
      sheet.getColumn(5).width = 15;

      // Auto-width
      sheet.columns.forEach((col) => {
        if (!col.width || col.width < 15) col.width = 18;
      });

      const buffer = await workbook.xlsx.writeBuffer();
      return buffer as ArrayBuffer;
    },
    [],
  );

  const handleDownloadReport = async () => {
    const buffer = await generateExcelReport(displayExpenses);
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    saveAs(blob, `expense_report_${new Date().toISOString().split('T')[0]}.xlsx`);
    setSuccess('Report downloaded');
  };

  const handleEmailReport = async () => {
    if (!graphClient) return;
    setSubmitting(true);
    setError(null);

    try {
      const buffer = await generateExcelReport(displayExpenses);
      const financeEmail =
        import.meta.env.VITE_FINANCE_EMAIL ?? 'finance@company.com';

      await sendMail(graphClient, {
        to: financeEmail,
        subject: `Expense Report - ${new Date().toLocaleDateString('en-GB')}`,
        htmlBody: `<p>Please find attached the expense report.</p><p>Total expenses: ${displayExpenses.length}</p><p>Generated by Prism</p>`,
        attachmentName: `expense_report_${new Date().toISOString().split('T')[0]}.xlsx`,
        attachmentContent: buffer,
      });

      setSuccess('Report emailed to finance');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to email');
    } finally {
      setSubmitting(false);
    }
  };

  const columns: Column<SPListItem<Expense>>[] = [
    {
      key: 'date',
      label: 'Date',
      sortable: true,
      getValue: (item) => item.fields.ExpenseDate,
      render: (item) => <Caption1>{formatDate(item.fields.ExpenseDate)}</Caption1>,
    },
    ...(canViewAll
      ? [
          {
            key: 'employee',
            label: 'Employee',
            getValue: (item: SPListItem<Expense>) => item.fields.EmployeeEmail,
            render: (item: SPListItem<Expense>) => (
              <Caption1>{item.fields.EmployeeEmail.split('@')[0]}</Caption1>
            ),
          },
        ]
      : []),
    {
      key: 'category',
      label: 'Category',
      render: (item) => (
        <Badge appearance="outline" size="small">
          {item.fields.Category}
        </Badge>
      ),
      getValue: (item) => item.fields.Category,
    },
    {
      key: 'description',
      label: 'Description',
      getValue: (item) => item.fields.Description,
      render: (item) => (
        <Body2 style={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {item.fields.Description}
        </Body2>
      ),
    },
    {
      key: 'amount',
      label: 'Amount',
      sortable: true,
      getValue: (item) => item.fields.Amount,
      render: (item) => (
        <Body1 style={{ fontWeight: 600 }}>
          {formatCurrency(item.fields.Amount)}
        </Body1>
      ),
    },
    {
      key: 'photo',
      label: 'Bill',
      render: (item) =>
        item.fields.BillPhotoUrl ? (
          <a
            href={item.fields.BillPhotoUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
          >
            <Camera24Regular />
          </a>
        ) : (
          <Caption1>—</Caption1>
        ),
    },
    {
      key: 'status',
      label: 'Status',
      render: (item) => (
        <Badge
          appearance="filled"
          color={STATUS_COLORS[item.fields.Status]}
          size="small"
        >
          {item.fields.Status}
        </Badge>
      ),
      getValue: (item) => item.fields.Status,
      sortable: true,
    },
    ...(activeTab === 'approval' && canApprove
      ? [
          {
            key: 'actions',
            label: 'Actions',
            render: (item: SPListItem<Expense>) => (
              <div className={styles.approvalActions}>
                <Button
                  appearance="primary"
                  size="small"
                  icon={<Checkmark24Regular />}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleApprove(item);
                  }}
                >
                  Approve
                </Button>
                <Button
                  appearance="outline"
                  size="small"
                  icon={<Dismiss24Regular />}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleReject(item);
                  }}
                >
                  Reject
                </Button>
              </div>
            ),
          },
        ]
      : []),
  ];

  const openNewForm = () => {
    setFormData({
      Amount: 0,
      Currency: DEFAULT_CURRENCY,
      Category: 'Other',
      Description: '',
      ExpenseDate: new Date().toISOString().split('T')[0],
      NeedsApproval: true,
      Notes: '',
    });
    setPhotoPreview(null);
    setPhotoFile(null);
    setShowForm(true);
  };

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <PageTitle>Expenses</PageTitle>
      </div>

      {error && (
        <MessageBar intent="error">
          <MessageBarBody>{error}</MessageBarBody>
        </MessageBar>
      )}
      {success && (
        <MessageBar intent="success">
          <MessageBarBody>{success}</MessageBarBody>
        </MessageBar>
      )}

      <TabList
        selectedValue={activeTab}
        onTabSelect={(_, d) => setActiveTab(d.value as string)}
      >
        <Tab value="my">
          My Expenses ({myExpenses.length})
        </Tab>
        {canApprove && (
          <Tab value="approval">
            Approval Queue ({pendingApproval.length})
          </Tab>
        )}
        {canViewAll && (
          <Tab value="all">
            All Expenses ({allExpenses.length})
          </Tab>
        )}
      </TabList>

      <DataTable
        columns={columns}
        items={displayExpenses}
        loading={loading}
        searchPlaceholder="Search expenses..."
        searchFields={(item) =>
          `${item.fields.Description} ${item.fields.Category} ${item.fields.EmployeeEmail}`
        }
        getRowKey={(item) => item.id}
        emptyMessage={
          activeTab === 'approval'
            ? 'No expenses pending approval'
            : 'No expenses found. Submit one to get started.'
        }
        actions={
          <div style={{ display: 'flex', gap: 8 }}>
            {displayExpenses.length > 0 && (
              <Button
                appearance="outline"
                icon={<ArrowDownload24Regular />}
                onClick={handleDownloadReport}
              >
                Download Excel
              </Button>
            )}
            {canEmail && displayExpenses.length > 0 && (
              <Button
                appearance="outline"
                icon={submitting ? <Spinner size="tiny" /> : <Mail24Regular />}
                disabled={submitting}
                onClick={handleEmailReport}
              >
                Email to Finance
              </Button>
            )}
            <Button
              appearance="primary"
              icon={<Add24Regular />}
              onClick={openNewForm}
            >
              Submit Expense
            </Button>
          </div>
        }
      />

      {/* Submit Expense Dialog */}
      <Dialog
        open={showForm}
        onOpenChange={() => setShowForm(false)}
      >
        <DialogSurface>
          <DialogBody>
            <DialogTitle>Submit Expense</DialogTitle>
            <DialogContent>
              <div className={styles.form}>
                <div className={styles.row}>
                  <Field label="Amount" required>
                    <Input
                      type="number"
                      value={String(formData.Amount)}
                      onChange={(_, d) =>
                        setFormData((f) => ({
                          ...f,
                          Amount: Number(d.value) || 0,
                        }))
                      }
                      contentBefore={<span>£</span>}
                    />
                  </Field>
                  <Field label="Date">
                    <Input
                      type="date"
                      value={formData.ExpenseDate}
                      onChange={(_, d) =>
                        setFormData((f) => ({ ...f, ExpenseDate: d.value }))
                      }
                    />
                  </Field>
                </div>
                <Field label="Category">
                  <Dropdown
                    value={formData.Category}
                    onOptionSelect={(_, d) =>
                      setFormData((f) => ({
                        ...f,
                        Category: d.optionValue as ExpenseCategory,
                      }))
                    }
                  >
                    {EXPENSE_CATEGORIES.map((c) => (
                      <Option key={c} value={c}>
                        {c}
                      </Option>
                    ))}
                  </Dropdown>
                </Field>
                <Field label="Description" required>
                  <Textarea
                    value={formData.Description}
                    onChange={(_, d) =>
                      setFormData((f) => ({ ...f, Description: d.value }))
                    }
                    resize="vertical"
                    placeholder="What was this expense for?"
                  />
                </Field>

                <Divider />

                {/* Bill Photo */}
                <Field label="Bill Photo">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    capture="environment"
                    onChange={handlePhotoCapture}
                    style={{ display: 'none' }}
                  />
                  {photoPreview ? (
                    <div style={{ textAlign: 'center' }}>
                      <Image
                        src={photoPreview}
                        className={styles.photoPreview}
                        alt="Bill preview"
                      />
                      <br />
                      <Button
                        appearance="subtle"
                        size="small"
                        onClick={() => {
                          setPhotoPreview(null);
                          setPhotoFile(null);
                        }}
                      >
                        Remove Photo
                      </Button>
                    </div>
                  ) : (
                    <div
                      className={styles.photoArea}
                      onClick={handleNativeCamera}
                    >
                      <Camera24Regular
                        style={{ fontSize: 32, marginBottom: 8 }}
                      />
                      <br />
                      <Caption1>
                        Tap to take photo or select from gallery
                      </Caption1>
                    </div>
                  )}
                </Field>

                <Field label="Notes (optional)">
                  <Input
                    value={formData.Notes}
                    onChange={(_, d) =>
                      setFormData((f) => ({ ...f, Notes: d.value }))
                    }
                    placeholder="Any additional notes"
                  />
                </Field>
              </div>
            </DialogContent>
            <DialogActions>
              <DialogTrigger disableButtonEnhancement>
                <Button appearance="secondary">Cancel</Button>
              </DialogTrigger>
              <Button
                appearance="primary"
                onClick={handleSubmit}
                disabled={submitting}
                icon={submitting ? <Spinner size="tiny" /> : undefined}
              >
                {submitting ? 'Submitting...' : 'Submit'}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
}
